import React from "react";
import PropTypes from "prop-types";
import Image from "next/image";
import styled, { withTheme } from "styled-components";
import UnifiedSearch from "components/UnifiedSearch";
import dyanmicShortCodeText from "components/Contentful/utils";

const FormWrapper = styled.div`
  background-color: ${({ theme }) => theme.themeColors.white};
  border: 0.0625rem solid ${({ theme }) => theme.themeColors.darkGreen4};
  border-radius: 1.9375rem;
  grid-row-start: 2;
  padding: 2rem;

  ${({ theme }) => theme.mq.lg`
    grid-row-start: auto;
    margin: 5.0625rem 0rem 5.75rem 0rem;
  `}
`;

const InnerWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 2.625rem;
  max-width: 76.25rem;
  width: 100%;

  ${({ theme }) => theme.mq.lg`
    grid-template-columns: 30.95rem 1fr;
  `}
`;

const OuterWrapper = styled.section`
  background-color: ${({ theme }) => theme.themeColors.lightGreen1};
  display: flex;
  justify-content: center;
  padding: 3.375rem 1.5625rem 1.25rem 1.5625rem;
  width: 100%;
  background-image: url(${({ backgroundImage }) => backgroundImage});
  background-size: cover;

  ${({ theme }) => theme.mq.lg`
    padding: 0rem 1.5625rem 0rem 1.5625rem;
  `}
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  font-size: 2.625rem;
  line-height: 3.125rem;
  margin-bottom: 1.25rem;
  text-align: left;

  ${({ theme }) => theme.mq.lg`
    font-size: ${theme.fontSize(7)};
    line-height: 4.8125rem;
    text-align: center;
  `}
`;

const locationLookup = {
  'austin': {
    formattedAddress: 'Austin, TX',
    latitude: 30.267153,
    longitude: -97.7430608,
    radius: 20,
  },
  'dc': {
    formattedAddress: 'Washington, DC',
    latitude: 38.9071923,
    longitude: -77.0368707,
    radius: 10,
  },
  'denver': {
    formattedAddress: 'Denver, CO',
    latitude: 39.7392358,
    longitude: -104.990251,
    radius: 20,
  },
  'new-york': {
    formattedAddress: 'New York, NY',
    latitude: 40.7127753,
    longitude: -74.0059728,
    radius: 10,
  },
  'philadelphia': {
    formattedAddress: 'Philadelphia, PA',
    latitude: 39.9525839,
    longitude: -75.1652215,
    radius: 20,
  },
  'san-antonio': {
    formattedAddress: 'San Antonio, TX',
    latitude: 29.4251905,
    longitude: -98.4945922,
    radius: 50,
  },
  'san-francisco': {
    formattedAddress: 'San Francisco, CA', 
    latitude: 37.7749295, 
    longitude: -122.4194155, 
    radius: 10,
  },
};

function LocationPageSearchBlock({
  backgroundImage,
  blockData,
  cityName,
  service,
  theme,
}) {
  const contentfulVarDict = {
    city: cityName,
    serviceType: service === "vets" ? service : `pet ${service}`,
  };

  const blockTitle = dyanmicShortCodeText({
    textToSearch: blockData?.pageTitle,
    replaceDictLookup: contentfulVarDict,
  });

  const contentfulServiceDict = {
    groomers: "grooming",
    trainers: "training",
    vets: "veterinary",
  };
  const defaultServiceSearch = contentfulServiceDict[service];
  const cityHandle = cityName.trim().toLowerCase().replace(' ', '-');

  const imageUrl = blockData?.image?.fields?.file?.url;
  const imageHeight = blockData?.image?.fields?.file?.details?.image?.height;
  const imageWidth = blockData?.image?.fields?.file?.details?.image?.width;
  const imageAltText = blockData?.image?.fields?.file?.fileName;

  return (
    <OuterWrapper backgroundImage={backgroundImage}>
      <InnerWrapper>
        <FormWrapper>
          <Title>{blockTitle}</Title>
          <UnifiedSearch theme={theme} location={locationLookup[cityHandle]} serviceType={defaultServiceSearch} />
        </FormWrapper>
        {blockData?.image && (
          <Image
            alt={imageAltText}
            height={imageHeight}
            width={imageWidth}
            src={`https:${imageUrl}`}
          />
        )}
      </InnerWrapper>
    </OuterWrapper>
  );
}

export default withTheme(LocationPageSearchBlock);

LocationPageSearchBlock.propTypes = {
  service: PropTypes.string,
  input: PropTypes.shape({ onChange: PropTypes.func }),
  blockData: PropTypes.shape({
    pageTitle: PropTypes.string,
    subtitle: PropTypes.string,
    image: PropTypes.shape({
      fields: PropTypes.shape({
        title: PropTypes.string,
        file: PropTypes.shape({
          fileName: PropTypes.string,
          contentType: PropTypes.string,
          url: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      }),
      sys: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }),
  theme: PropTypes.shape({
    themeColors: PropTypes.shape({
      darkGreen1: PropTypes.string,
    }),
  }),
  backgroundImage: PropTypes.string,
  cityName: PropTypes.string,
};

LocationPageSearchBlock.defaultProps = {
  backgroundImage: "",
  blockData: undefined,
  cityName: "",
  input: {},
  service: "groomers",
  theme: undefined,
};
